import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Bell,
  CheckCircle2,
  ChevronRight,
  FilePlus2,
  ExternalLink,
  Home,
  Lightbulb,
  MessageCircle,
  Paperclip,
  Plus,
  Send,
  Settings,
  Sparkles,
  Trash2,
} from "lucide-react";
import type { Proposal, PublicUser } from "@serezha168/shared";
import {
  PROPOSAL_STATUS_LABELS,
  MAX_ATTACHMENT_SIZE,
  MAX_ATTACHMENTS,
  MAX_TOTAL_ATTACHMENT_SIZE,
  canEditProposal,
} from "@serezha168/shared";
import { api } from "../api";
import { useAsync } from "../hooks";
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  Spinner,
  Toast,
  formatDate,
} from "../components/Ui";

export function UserLayout({ user }: { user: PublicUser }) {
  return (
    <div className="user-shell">
      <header className="user-header">
        <Link to="/" className="brand">
          <span className="brand__mark">S</span>
          <span>SEREZHA168</span>
        </Link>
        <Link to="/settings" aria-label="Настройки">
          <Avatar name={user.firstName} src={user.photoUrl} size={36} />
        </Link>
      </header>
      <main className="user-main">
        <Outlet />
      </main>
      <nav className="bottom-nav">
        <NavLink to="/" end>
          <Home size={20} />
          <span>Главная</span>
        </NavLink>
        <NavLink to="/ideas">
          <Lightbulb size={20} />
          <span>Идеи</span>
        </NavLink>
        <NavLink to="/contact">
          <MessageCircle size={20} />
          <span>Написать</span>
        </NavLink>
        <NavLink to="/settings">
          <Settings size={20} />
          <span>Настройки</span>
        </NavLink>
      </nav>
    </div>
  );
}

export function ConsentPage({
  user,
  onAccepted,
}: {
  user: PublicUser;
  onAccepted(): Promise<unknown>;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [broadcastEnabled, setBroadcastEnabled] = useState(true);
  const accept = async () => {
    setLoading(true);
    setError("");
    try {
      await api.consent(broadcastEnabled);
      await onAccepted();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Ошибка");
    } finally {
      setLoading(false);
    }
  };
  return (
    <main className="consent-page">
      <div className="consent-visual">
        <Sparkles size={44} />
        <span>SEREZHA168</span>
      </div>
      <div>
        <p className="eyebrow">Привет, {user.firstName}</p>
        <h1>Перед началом</h1>
        <p className="lead">
          Здесь можно предложить идею для видео и написать мне лично.
        </p>
      </div>
      <Card className="consent-card">
        <h2>Как хранятся данные</h2>
        <ul className="check-list">
          <li>
            <CheckCircle2 /> Сохраняются публичные данные Telegram: имя,
            username и фото.
          </li>
          <li>
            <CheckCircle2 /> Идеи и переписка остаются в моей админ-панели для
            сохранения контекста.
          </li>
          <li>
            <CheckCircle2 /> Удаление чата у себя в Telegram не удаляет
            серверную копию.
          </li>
          <li>
            <CheckCircle2 /> Массовые сообщения можно отключить в настройках.
          </li>
        </ul>
      </Card>
      <label className="consent-option">
        <input
          type="checkbox"
          checked={broadcastEnabled}
          onChange={(event) => setBroadcastEnabled(event.target.checked)}
        />
        <span>
          <strong>Получать общие рассылки</strong>
          <small>
            Новости и массовые сообщения. Можно отключить позже в настройках.
          </small>
        </span>
      </label>
      {error ? <p className="form-error">{error}</p> : null}
      <Button onClick={accept} loading={loading}>
        Понятно, продолжить
      </Button>
      <p className="fine-print">
        Продолжая, вы соглашаетесь на описанное хранение данных. Телефон и
        непубличные сведения не запрашиваются.
      </p>
    </main>
  );
}

export function HomePage({ user }: { user: PublicUser }) {
  const proposals = useAsync(() => api.proposals(), []);
  const latest = proposals.data?.proposals[0];
  return (
    <div className="page-stack">
      <section className="hero">
        <p className="eyebrow">Привет, {user.firstName}</p>
        <h1>Есть идея для нового видео?</h1>
        <p>Расскажите — я увижу предложение лично и смогу ответить.</p>
        <Link className="button button--primary" to="/ideas/new">
          <Plus size={18} />
          Предложить идею
        </Link>
      </section>
      <div className="quick-grid">
        <Link to="/ideas" className="quick-card">
          <div className="quick-card__icon violet">
            <Lightbulb />
          </div>
          <div>
            <strong>Мои идеи</strong>
            <span>{proposals.data?.proposals.length ?? 0} отправлено</span>
          </div>
          <ChevronRight />
        </Link>
        <Link to="/contact" className="quick-card">
          <div className="quick-card__icon blue">
            <MessageCircle />
          </div>
          <div>
            <strong>Написать мне</strong>
            <span>Начать диалог</span>
          </div>
          <ChevronRight />
        </Link>
      </div>
      {latest ? (
        <Card>
          <div className="section-heading">
            <div>
              <span className="eyebrow">Последняя идея</span>
              <h2>{latest.title}</h2>
            </div>
            <StatusBadge status={latest.status} />
          </div>
          <p className="clamp">{latest.description}</p>
          <Link className="text-link" to="/ideas">
            Посмотреть все <ChevronRight size={16} />
          </Link>
        </Card>
      ) : null}
    </div>
  );
}

function StatusBadge({ status }: { status: Proposal["status"] }) {
  return (
    <span className={`status status--${status}`}>
      {PROPOSAL_STATUS_LABELS[status]}
    </span>
  );
}

export function ProposalsPage() {
  const state = useAsync(() => api.proposals(), []);
  if (state.loading) return <Spinner />;
  return (
    <div className="page-stack">
      <div className="page-title">
        <div>
          <p className="eyebrow">Предложка</p>
          <h1>Мои идеи</h1>
        </div>
        <Link className="icon-button icon-button--accent" to="/ideas/new">
          <Plus />
        </Link>
      </div>
      {state.error ? <p className="form-error">{state.error}</p> : null}
      {!state.data?.proposals.length ? (
        <EmptyState
          icon={<Lightbulb />}
          title="Пока нет идей"
          text="Предложите тему, историю или формат для следующего видео."
          action={
            <Link className="button button--primary" to="/ideas/new">
              Предложить идею
            </Link>
          }
        />
      ) : (
        <div className="proposal-list">
          {state.data.proposals.map((proposal) => (
            <ProposalCard key={proposal.id} proposal={proposal} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProposalCard({ proposal }: { proposal: Proposal }) {
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentName, setAttachmentName] = useState("");
  const openAttachment = async (id: string, name: string) => {
    const blob = await api.attachmentFile(id);
    if (attachmentUrl) URL.revokeObjectURL(attachmentUrl);
    setAttachmentName(name);
    setAttachmentUrl(URL.createObjectURL(blob));
  };
  return (
    <Card className="proposal-card">
      <div className="proposal-card__top">
        <StatusBadge status={proposal.status} />
        <time>{formatDate(proposal.createdAt)}</time>
      </div>
      <h2>{proposal.title}</h2>
      <p>{proposal.description}</p>
      {proposal.links.length ? (
        <div className="proposal-links">
          {proposal.links.map((link) => (
            <a key={link} href={link} target="_blank" rel="noreferrer">
              <ExternalLink size={14} />
              <span>{link}</span>
            </a>
          ))}
        </div>
      ) : null}
      {proposal.attachments.length ? (
        <div className="proposal-attachments">
          {proposal.attachments.map((attachment) => (
            <button
              key={attachment.id}
              type="button"
              onClick={() =>
                void openAttachment(attachment.id, attachment.name)
              }
            >
              <Paperclip size={15} />
              <span>{attachment.name}</span>
              <small>{(attachment.size / 1024 / 1024).toFixed(1)} МБ</small>
            </button>
          ))}
        </div>
      ) : null}
      {proposal.adminComment ? (
        <div className="admin-comment">
          <strong>Ответ</strong>
          <p>{proposal.adminComment}</p>
        </div>
      ) : null}
      {canEditProposal(proposal.status) ? (
        <Link
          className="button button--secondary button--compact"
          to={`/ideas/${proposal.id}/edit`}
        >
          Редактировать
        </Link>
      ) : null}
      {attachmentUrl ? (
        <div className="attachment-download">
          <a
            className="button button--secondary button--compact"
            href={attachmentUrl}
            download={attachmentName}
          >
            Скачать {attachmentName}
          </a>
          <button
            type="button"
            onClick={() => {
              URL.revokeObjectURL(attachmentUrl);
              setAttachmentUrl("");
            }}
          >
            Закрыть
          </button>
        </div>
      ) : null}
    </Card>
  );
}

export function ProposalFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const state = useAsync(() => api.proposals(), []);
  const existing = id
    ? state.data?.proposals.find((proposal) => proposal.id === id)
    : undefined;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [linksText, setLinksText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [keptAttachmentIds, setKeptAttachmentIds] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setDescription(existing.description);
      setLinksText(existing.links.join("\n"));
      setKeptAttachmentIds(
        existing.attachments.map((attachment) => attachment.id),
      );
    }
  }, [existing]);
  const keptAttachments =
    existing?.attachments.filter((attachment) =>
      keptAttachmentIds.includes(attachment.id),
    ) ?? [];
  const totalSize = useMemo(
    () =>
      files.reduce((sum, file) => sum + file.size, 0) +
      keptAttachments.reduce((sum, attachment) => sum + attachment.size, 0),
    [files, keptAttachments],
  );

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const next = [...files, ...Array.from(incoming)];
    if (next.length + keptAttachmentIds.length > MAX_ATTACHMENTS)
      return setError("Можно добавить не больше 5 файлов");
    if (next.some((file) => file.size > MAX_ATTACHMENT_SIZE))
      return setError("Один файл должен быть не больше 20 МБ");
    if (
      next.reduce((sum, file) => sum + file.size, 0) +
        keptAttachments.reduce((sum, attachment) => sum + attachment.size, 0) >
      MAX_TOTAL_ATTACHMENT_SIZE
    )
      return setError("Общий размер должен быть не больше 50 МБ");
    setError("");
    setFiles(next);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const links = linksText
        .split(/\s+/)
        .map((value) => value.trim())
        .filter(Boolean);
      for (const link of links) new URL(link);
      const attachmentIds: string[] = [...keptAttachmentIds];
      for (const file of files) attachmentIds.push((await api.upload(file)).id);
      if (id)
        await api.updateProposal(id, {
          title,
          description,
          links,
          attachmentIds,
        });
      else
        await api.createProposal({ title, description, links, attachmentIds });
      window.Telegram?.WebApp.HapticFeedback?.impactOccurred("medium");
      navigate("/ideas");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Не удалось сохранить",
      );
    } finally {
      setLoading(false);
    }
  };
  if (id && state.loading) return <Spinner />;
  if (id && !existing)
    return (
      <EmptyState
        icon={<Lightbulb />}
        title="Идея не найдена"
        text="Возможно, она была удалена."
      />
    );
  return (
    <form className="page-stack" onSubmit={submit}>
      <div className="page-title">
        <div>
          <p className="eyebrow">Предложка</p>
          <h1>{id ? "Изменить идею" : "Новая идея"}</h1>
        </div>
      </div>
      <label className="field">
        <span>Название</span>
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          minLength={3}
          maxLength={120}
          placeholder="Коротко о чём видео"
          required
        />
      </label>
      <label className="field">
        <span>Расскажите подробнее</span>
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          minLength={10}
          maxLength={6000}
          rows={7}
          placeholder="Что должно произойти, почему это будет интересно…"
          required
        />
      </label>
      <label className="field">
        <span>
          Ссылки <small>по одной или через пробел</small>
        </span>
        <textarea
          value={linksText}
          onChange={(event) => setLinksText(event.target.value)}
          rows={3}
          placeholder="https://…"
        />
      </label>
      <div className="field">
        <span>
          Вложения <small>до 5 файлов, всего до 50 МБ</small>
        </span>
        <label className="dropzone">
          <FilePlus2 />
          <strong>Добавить фото, видео или файл</strong>
          <span>Большие видео лучше добавить ссылкой</span>
          <input
            type="file"
            multiple
            onChange={(event) => addFiles(event.target.files)}
          />
        </label>
        {keptAttachments.map((attachment) => (
          <div className="file-row" key={attachment.id}>
            <Paperclip />
            <span>{attachment.name}</span>
            <small>{(attachment.size / 1024 / 1024).toFixed(1)} МБ</small>
            <button
              type="button"
              onClick={() =>
                setKeptAttachmentIds(
                  keptAttachmentIds.filter((id) => id !== attachment.id),
                )
              }
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {files.map((file, index) => (
          <div className="file-row" key={`${file.name}-${index}`}>
            <Paperclip />
            <span>{file.name}</span>
            <small>{(file.size / 1024 / 1024).toFixed(1)} МБ</small>
            <button
              type="button"
              onClick={() =>
                setFiles(files.filter((_, fileIndex) => fileIndex !== index))
              }
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {keptAttachments.length || files.length ? (
          <p className="fine-print">
            Всего: {(totalSize / 1024 / 1024).toFixed(1)} МБ
          </p>
        ) : null}
      </div>
      {error ? <p className="form-error">{error}</p> : null}
      <Button type="submit" loading={loading}>
        {id ? "Сохранить изменения" : "Отправить идею"}
      </Button>
    </form>
  );
}

export function ContactPage() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.contact(text);
      setSent(true);
      setText("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Ошибка");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="page-stack">
      <div className="page-title">
        <div>
          <p className="eyebrow">Личная связь</p>
          <h1>Написать мне</h1>
        </div>
      </div>
      {sent ? (
        <Card className="success-card">
          <CheckCircle2 />
          <h2>Сообщение отправлено</h2>
          <p>
            Ответ придёт обычным сообщением от бота. После этого можно
            продолжать переписку прямо в Telegram и отвечать свайпом.
          </p>
          <Button variant="secondary" onClick={() => setSent(false)}>
            Написать ещё
          </Button>
        </Card>
      ) : (
        <form className="page-stack" onSubmit={submit}>
          <Card>
            <MessageCircle className="card-icon" />
            <h2>Первое сообщение</h2>
            <p>
              Опишите вопрос или тему. Я увижу ваше имя и всю дальнейшую
              переписку в одном диалоге.
            </p>
          </Card>
          <label className="field">
            <span>Ваше сообщение</span>
            <textarea
              rows={8}
              value={text}
              onChange={(event) => setText(event.target.value)}
              maxLength={4000}
              minLength={1}
              placeholder="Напишите здесь…"
              required
            />
          </label>
          {error ? <p className="form-error">{error}</p> : null}
          <Button type="submit" loading={loading}>
            <Send size={18} />
            Отправить
          </Button>
        </form>
      )}
    </div>
  );
}

export function SettingsPage({
  user,
  onChanged,
}: {
  user: PublicUser;
  onChanged(): Promise<unknown>;
}) {
  const [enabled, setEnabled] = useState(user.broadcastEnabled);
  const [toast, setToast] = useState("");
  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try {
      await api.setBroadcasts(next);
      await onChanged();
      setToast("Настройки сохранены");
    } catch (caught) {
      setEnabled(!next);
      setToast(caught instanceof Error ? caught.message : "Ошибка");
    }
  };
  return (
    <div className="page-stack">
      <div className="profile-card">
        <Avatar name={user.firstName} src={user.photoUrl} size={76} />
        <h1>
          {user.firstName} {user.lastName}
        </h1>
        {user.username ? <p>@{user.username}</p> : <p>Telegram ID {user.id}</p>}
      </div>
      <Card>
        <div className="setting-row">
          <div className="setting-row__icon">
            <Bell />
          </div>
          <div>
            <strong>Массовые рассылки</strong>
            <p>
              Новости и общие сообщения. Личные ответы будут приходить всегда.
            </p>
          </div>
          <button
            className={`switch ${enabled ? "switch--on" : ""}`}
            onClick={toggle}
            aria-label="Переключить рассылки"
          >
            <span />
          </button>
        </div>
      </Card>
      <Card>
        <h2>О данных</h2>
        <p>
          Идеи, публичные данные Telegram и переписка сохраняются у владельца
          бота для контекста. Удаление сообщений только у себя не удаляет
          серверную копию.
        </p>
      </Card>
      {toast ? <Toast message={toast} onClose={() => setToast("")} /> : null}
    </div>
  );
}
